/**
 * User preferences sync — bridges localStorage with the database.
 *
 * On mount: fetches server preferences and merges into local stores.
 * On change: debounced PUT to /preferences to persist across devices.
 *
 * When adding new user-facing settings:
 * 1. Add the key to UserPreferences interface below
 * 2. Include it in loadFromServer() to apply on login
 * 3. Include it in the relevant store's setter to push on change
 */
import { useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { useThemeStore } from '@/stores/theme-store';
import { useI18n } from '@/lib/i18n';
import type { ThemePresetId, ThemeMode } from '@/lib/theme-engine';

interface UserPreferences {
  themeMode?: ThemeMode;
  themePreset?: ThemePresetId;
  themeCustomColor?: string | null;
  fontSize?: 'small' | 'default' | 'large';
  compact?: boolean;
  locale?: string;
  notifications?: {
    emailNotifications?: boolean;
    matchNotifications?: boolean;
    assignmentAlerts?: boolean;
    weeklyDigest?: boolean;
  };
}

let synced = false;

/** Debounced PUT to /preferences. Fire-and-forget. */
let saveTimer: ReturnType<typeof setTimeout> | null = null;

function saveToServer(prefs: Partial<UserPreferences>): void {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    apiFetch('/preferences', {
      method: 'PUT',
      body: JSON.stringify(prefs)
    }).catch(() => {
      // Silent fail — localStorage is the fallback
    });
  }, 500);
}

/** Call once from AppLayout to sync preferences on login. */
export function usePreferencesSync(): void {
  const hasRun = useRef(false);

  const loadFromServer = useCallback(async () => {
    if (synced) return;
    synced = true;

    try {
      const res = await apiFetch<UserPreferences>('/preferences');
      if (!res.success || !res.data) return;
      const prefs = res.data;

      // Theme — only apply if server has values AND localStorage is at defaults
      const localMode = localStorage.getItem('eligaro-theme-mode');
      if (prefs.themeMode && !localMode) {
        useThemeStore.getState().setMode(prefs.themeMode);
      }
      if (prefs.themePreset && !localStorage.getItem('eligaro-theme-preset')) {
        useThemeStore.getState().setPreset(prefs.themePreset);
      }
      if (prefs.themeCustomColor && !localStorage.getItem('eligaro-theme-custom')) {
        useThemeStore.getState().setCustomColor(prefs.themeCustomColor);
      }

      // Locale
      if (prefs.locale && !localStorage.getItem('eligaro-locale')) {
        useI18n.getState().setLocale(prefs.locale as 'en' | 'sv');
      }

      // Font size + compact mode
      if (prefs.fontSize && !localStorage.getItem('eligaro-font-size')) {
        useThemeStore.getState().setFontSize(prefs.fontSize);
      }
      if (prefs.compact !== undefined && !localStorage.getItem('eligaro-compact')) {
        useThemeStore.getState().setCompact(prefs.compact);
      }

      // Notification prefs
      if (prefs.notifications && !localStorage.getItem('eligaro-notification-prefs')) {
        localStorage.setItem('eligaro-notification-prefs', JSON.stringify(prefs.notifications));
      }
    } catch {
      // Offline or not logged in — use localStorage values
    }
  }, []);

  useEffect(() => {
    if (!hasRun.current) {
      hasRun.current = true;
      loadFromServer();
    }
  }, [loadFromServer]);
}

/** Push current theme settings to server. Called by theme store setters. */
export function syncThemeToServer(
  mode: ThemeMode,
  preset: ThemePresetId,
  customColor: string | null,
  fontSize?: 'small' | 'default' | 'large',
  compact?: boolean
): void {
  saveToServer({
    themeMode: mode,
    themePreset: preset,
    themeCustomColor: customColor,
    fontSize,
    compact
  });
}

/** Push locale to server. Called by i18n store setter. */
export function syncLocaleToServer(locale: string): void {
  saveToServer({ locale });
}

/** Push notification prefs to server. Called by notification preferences tab. */
export function syncNotificationPrefsToServer(prefs: UserPreferences['notifications']): void {
  saveToServer({ notifications: prefs });
}
